/**
 * Links that sign a user in by phone: any AuthGuard-protected URL can carry
 * `?loginPhone=<E.164>`, e.g. `/main/live-session?id=6751&loginPhone=%2B15551234567`.
 * Instead of the login form, the user lands on verify-code with a code already
 * sent, and ends up on the URL they were sent, minus the parameter.
 */

export const LOGIN_PHONE_PARAM = 'loginPhone';

/**
 * The whole number as the login mutation takes it: `+`, then digits only.
 * Length is left to the backend, which accepts short test numbers (`+10`,
 * `+1333`) used for staging credentials; 15 is E.164's ceiling.
 */
const LOGIN_PHONE = /^\+\d{1,15}$/;

const PENDING_LOGIN_KEY = 'pendingLogin';

/**
 * The `loginPhone` in a query string, or null when absent or malformed.
 *
 * A literal `+` in a query string decodes to a space, so a leading space is
 * read back as the `+` it was; links should still encode it as `%2B`.
 */
export function parseLoginPhone(search: string): string | null {
    const raw = new URLSearchParams(search).get(LOGIN_PHONE_PARAM);
    if (!raw) return null;
    const phone = raw.startsWith(' ') ? `+${raw.slice(1)}` : raw;
    return LOGIN_PHONE.test(phone) ? phone : null;
}

/** The query string without `loginPhone`: `''` if nothing is left, else `?…`. */
export function stripLoginPhone(search: string): string {
    const params = new URLSearchParams(search);
    params.delete(LOGIN_PHONE_PARAM);
    const rest = params.toString();
    return rest ? `?${rest}` : '';
}

/** Same number, ignoring formatting. */
export function isSamePhone(a?: string | null, b?: string | null): boolean {
    const digits = (value?: string | null) => (value ?? '').replace(/\D/g, '');
    return Boolean(digits(a)) && digits(a) === digits(b);
}

/**
 * The login in progress between sending a code and verifying it. Kept in
 * sessionStorage (this tab only) so verify-code survives a refresh; router
 * state alone does not.
 */
export interface PendingLogin {
    phoneNumber: string;
    /** Set by a `loginPhone` link: verify-code sends the code itself, once. */
    autoSend: boolean;
    /** Came from a `loginPhone` link; unlike `autoSend`, stays set once sent. */
    fromLink?: boolean;
    isEmailVerified?: boolean;
}

export function savePendingLogin(pending: PendingLogin): void {
    sessionStorage.setItem(PENDING_LOGIN_KEY, JSON.stringify(pending));
}

export function getPendingLogin(): PendingLogin | null {
    try {
        const pending = JSON.parse(sessionStorage.getItem(PENDING_LOGIN_KEY) ?? 'null');
        return pending && typeof pending.phoneNumber === 'string' ? pending : null;
    } catch {
        return null;
    }
}

export function clearPendingLogin(): void {
    sessionStorage.removeItem(PENDING_LOGIN_KEY);
}
